import {
  InfiniteData,
  Query,
  QueryClient,
  QueryKey,
  replaceEqualDeep,
} from '@tanstack/react-query'
import {
  getPostRow,
  isPostAccessError,
  postFeedQueryKey,
  postPinnedQueryKey,
  postRowEntityKey,
  postRowQueries,
  postRowsQueryKey,
  postScheduledQueryKey,
} from '../../api/posts'
import { discussionQueryKey } from '../../api/comments'
import { peopleQueryKey } from '../../api/people'
import { pollQueries } from '../../api/polls'
import { eventQueries } from '../../api/events'
import { boardRoomEventQueries } from '../../api/boardroom-events'
import {
  EventUpdatedPayload,
  PostUpdatedPayload,
} from '../../generated/api/live-events'
import {
  PostEventAttendance,
  PostPollCard,
  PostRow,
  PostsPage,
} from '../../generated/api/post-row'

type RowUpdate = (row: PostRow) => PostRow

export const applyToRows = (
  rows: PostRow[],
  postId: string,
  update: RowUpdate
): PostRow[] =>
  rows.some((row) => row.id === postId)
    ? rows.map((row) => (row.id === postId ? update(row) : row))
    : rows

export const dropRow = (rows: PostRow[], postId: string): PostRow[] =>
  rows.filter((row) => row.id !== postId)

export const likedRow = (row: PostRow, liked: boolean): PostRow =>
  row.likes.viewerLiked === liked
    ? row
    : {
        ...row,
        likes: {
          viewerLiked: liked,
          count: Math.max(0, row.likes.count + (liked ? 1 : -1)),
        },
      }

export const commentedRow = (row: PostRow, delta: number): PostRow => ({
  ...row,
  comments: {
    count: Math.max(0, row.comments.count + delta),
    viewerCommented: delta > 0 ? true : row.comments.viewerCommented,
  },
})

export const pinnedRow = (row: PostRow, pinned: boolean): PostRow =>
  row.isPinned === pinned ? row : { ...row, isPinned: pinned }

export const rowWithPoll = (row: PostRow, poll: PostPollCard): PostRow => ({
  ...row,
  polls: row.polls.map((card) => (card.id === poll.id ? poll : card)),
})

const mapPages = (
  data: InfiniteData<PostsPage, string | undefined> | undefined,
  mapItems: (items: PostRow[]) => PostRow[]
): InfiniteData<PostsPage, string | undefined> | undefined => {
  if (data == null) {
    return data
  }
  const pages = data.pages.map((page) => {
    const items = mapItems(page.items)
    return items === page.items ? page : { ...page, items }
  })
  return pages.every((page, index) => page === data.pages[index])
    ? data
    : { ...data, pages }
}

// A local patch is no fetch: it keeps the fetch time and a pending refetch.
export const patchQueries = <T>(
  client: QueryClient,
  queryKey: QueryKey,
  update: (data: T) => T | undefined
): void =>
  client.getQueriesData<T>({ queryKey }).forEach(([key, data]) => {
    const next = data == null ? undefined : update(data)
    if (next === undefined || replaceEqualDeep(data, next) === data) {
      return
    }
    const state = client.getQueryState(key)
    client.setQueryData<T>(key, next, { updatedAt: state?.dataUpdatedAt })
    if (state?.isInvalidated) {
      client.invalidateQueries({
        queryKey: key,
        exact: true,
        refetchType: 'none',
      })
    }
  })

export const updatePostRow = (
  client: QueryClient,
  postId: string,
  update: RowUpdate
): void => {
  patchQueries<InfiniteData<PostsPage, string | undefined>>(
    client,
    postFeedQueryKey,
    (data) => mapPages(data, (items) => applyToRows(items, postId, update))
  )
  patchQueries<PostRow[]>(client, postPinnedQueryKey, (rows) =>
    applyToRows(rows, postId, update)
  )
  patchQueries<PostRow[]>(client, postScheduledQueryKey, (rows) =>
    applyToRows(rows, postId, update)
  )
  patchQueries<PostRow>(client, postRowQueries.row(postId).queryKey, update)
}

type AnsweredEvent = {
  attendanceEnabled?: boolean
  attendance?: {
    userId: string
    status: string
    comment?: string
  }[]
}

export const viewerAttendance = (
  event: AnsweredEvent,
  viewerId: string
): PostEventAttendance => {
  const enabled = event.attendanceEnabled === true
  const own = !enabled
    ? undefined
    : event.attendance?.find((attendance) => attendance.userId === viewerId)
  return {
    enabled,
    viewerStatus: own?.status as PostEventAttendance['viewerStatus'],
    viewerComment: own?.comment || undefined,
  }
}

const carriesEvent = (row: PostRow, eventId: string): boolean =>
  row.events.some((event) => event.id === eventId)

export const rowWithEventAttendance = (
  row: PostRow,
  eventId: string,
  attendance: PostEventAttendance
): PostRow =>
  carriesEvent(row, eventId)
    ? {
        ...row,
        events: row.events.map((event) =>
          event.id === eventId ? { ...event, attendance } : event
        ),
      }
    : row

export const updateEventAttendance = (
  client: QueryClient,
  eventId: string,
  attendance: PostEventAttendance
): void => {
  const update = (rows: PostRow[]): PostRow[] =>
    rows.some((row) => carriesEvent(row, eventId))
      ? rows.map((row) => rowWithEventAttendance(row, eventId, attendance))
      : rows
  patchQueries<InfiniteData<PostsPage, string | undefined>>(
    client,
    postFeedQueryKey,
    (data) => mapPages(data, update)
  )
  patchQueries<PostRow[]>(client, postPinnedQueryKey, update)
  patchQueries<PostRow>(client, postRowEntityKey, (row) =>
    rowWithEventAttendance(row, eventId, attendance)
  )
}

export type PinnedSnapshot = ReturnType<typeof snapshotPinnedRows>

export const snapshotPinnedRows = (
  client: QueryClient
): [QueryKey, PostRow[] | undefined][] =>
  client.getQueriesData<PostRow[]>({ queryKey: postPinnedQueryKey })

export const restorePinnedRows = (
  client: QueryClient,
  snapshot: PinnedSnapshot
): void => {
  snapshot.forEach(([key, rows]) => client.setQueryData<PostRow[]>(key, rows))
}

export const removePinnedRow = (client: QueryClient, postId: string): void =>
  patchQueries<PostRow[]>(client, postPinnedQueryKey, (rows) =>
    dropRow(rows, postId)
  )

export const removePostFromLists = (
  client: QueryClient,
  postId: string
): void => {
  patchQueries<InfiniteData<PostsPage, string | undefined>>(
    client,
    postFeedQueryKey,
    (data) => mapPages(data, (items) => dropRow(items, postId))
  )
  removePinnedRow(client, postId)
  patchQueries<PostRow[]>(client, postScheduledQueryKey, (rows) =>
    dropRow(rows, postId)
  )
}

export const removePostRow = (client: QueryClient, postId: string): void => {
  removePostFromLists(client, postId)
  client.removeQueries({ queryKey: postRowQueries.row(postId).queryKey })
}

export const withdrawPost = (client: QueryClient, postId: string): void => {
  removePostFromLists(client, postId)
  client.resetQueries({ queryKey: postRowQueries.row(postId).queryKey })
  client.removeQueries({
    queryKey: discussionQueryKey(postId),
    predicate: (query) => query.getObserversCount() === 0,
  })
  client.removeQueries({
    queryKey: peopleQueryKey({ kind: 'post-likers', id: postId }),
  })
  client.removeQueries({
    queryKey: peopleQueryKey({ kind: 'post-readers', id: postId }),
  })
}

export const refreshPostRow = (
  client: QueryClient,
  postId: string
): Promise<void> =>
  getPostRow(postId).then(
    (response) => updatePostRow(client, postId, () => response.data.data),
    (error) => {
      if (isPostAccessError(error)) {
        withdrawPost(client, postId)
      }
    }
  )

export const refreshRowsHolding = (
  client: QueryClient,
  holds: (row: PostRow) => boolean
): Promise<void[]> => {
  const rows = [
    ...client
      .getQueriesData<InfiniteData<PostsPage, string | undefined>>({
        queryKey: postFeedQueryKey,
      })
      .flatMap(([, data]) => data?.pages.flatMap((page) => page.items) ?? []),
    ...client
      .getQueriesData<PostRow[]>({ queryKey: postPinnedQueryKey })
      .flatMap(([, pinned]) => pinned ?? []),
    ...(client.getQueryData<PostRow[]>(postScheduledQueryKey) ?? []),
    ...client
      .getQueriesData<PostRow>({ queryKey: postRowEntityKey })
      .flatMap(([, row]) => row ?? []),
  ]
  const postIds = new Set(rows.filter(holds).map((row) => row.id))
  return Promise.all(
    [...postIds].map((postId) => refreshPostRow(client, postId))
  )
}

export const invalidatePostRows = (client: QueryClient): void => {
  client.invalidateQueries({ queryKey: postRowsQueryKey })
}

export const cancelPostRows = async (
  client: QueryClient
): Promise<QueryKey[]> => {
  const interrupted = client
    .getQueryCache()
    .findAll({
      queryKey: postRowsQueryKey,
      predicate: (query) => query.state.fetchStatus !== 'idle',
    })
    .map((query) => query.queryKey)
  await client.cancelQueries({ queryKey: postRowsQueryKey })
  return interrupted
}

export const resumePostRows = (
  client: QueryClient,
  interrupted: QueryKey[] = []
): void => {
  interrupted.forEach((queryKey) =>
    client.invalidateQueries(
      { queryKey, exact: true },
      { cancelRefetch: false }
    )
  )
}

export const invalidatePublishedPost = (
  client: QueryClient,
  postId?: string
): void => {
  client.invalidateQueries({ queryKey: postScheduledQueryKey })
  client.invalidateQueries({ queryKey: postFeedQueryKey })
  client.invalidateQueries({ queryKey: postPinnedQueryKey })
  if (postId) {
    client.invalidateQueries({ queryKey: postRowQueries.row(postId).queryKey })
  }
}

const containsPost = (rows: PostRow[] | undefined, postId: string): boolean =>
  rows?.some((row) => row.id === postId) ?? false

export const isPostCached = (
  client: QueryClient,
  postId: string,
  { inPinned }: { inPinned: boolean }
): boolean =>
  client
    .getQueriesData<InfiniteData<PostsPage, string | undefined>>({
      queryKey: postFeedQueryKey,
    })
    .some(([, data]) =>
      data?.pages.some((page) => containsPost(page.items, postId))
    ) ||
  (inPinned &&
    client
      .getQueriesData<PostRow[]>({ queryKey: postPinnedQueryKey })
      .some(([, rows]) => containsPost(rows, postId))) ||
  containsPost(client.getQueryData<PostRow[]>(postScheduledQueryKey), postId) ||
  client.getQueryData(postRowQueries.row(postId).queryKey) !== undefined

const listsSociety =
  (societyId: string) =>
  ({ queryKey }: Query): boolean => {
    const [{ filter }] = queryKey as [{ filter?: { societies: string[] } }]
    return !filter?.societies.length || filter.societies.includes(societyId)
  }

export const applyPostUpdated = async (
  client: QueryClient,
  { societyId, postId, action, pollId }: PostUpdatedPayload
): Promise<void> => {
  if (action === 'created') {
    await client.invalidateQueries({
      queryKey: postFeedQueryKey,
      predicate: listsSociety(societyId),
    })
  } else if (action === 'scheduled') {
    await client.invalidateQueries({ queryKey: postScheduledQueryKey })
  } else if (action === 'deleted') {
    withdrawPost(client, postId)
  } else {
    const pinChange = action === 'pinned' || action === 'unpinned'
    await Promise.all([
      isPostCached(client, postId, { inPinned: !pinChange }) &&
        refreshPostRow(client, postId),
      pinChange &&
        client.invalidateQueries({
          queryKey: postPinnedQueryKey,
          predicate: listsSociety(societyId),
        }),
      action === 'unpinned' &&
        client.invalidateQueries({
          queryKey: postFeedQueryKey,
          predicate: listsSociety(societyId),
        }),
      action === 'edited' &&
        client.invalidateQueries({ queryKey: discussionQueryKey(postId) }),
      action === 'liked' &&
        client.invalidateQueries({
          queryKey: peopleQueryKey({ kind: 'post-likers', id: postId }),
        }),
      pollId &&
        client.invalidateQueries({
          queryKey: pollQueries.card(pollId).queryKey,
        }),
    ])
  }
}

const refetchedData = <T>(
  client: QueryClient,
  queryKey: QueryKey
): T | undefined => {
  const state = client.getQueryState<T>(queryKey)
  return state?.isInvalidated === false ? state.data : undefined
}

export const applyEventUpdated = async (
  client: QueryClient,
  { societyId, eventId, kind, postIds, action }: EventUpdatedPayload,
  viewerId: string | undefined
): Promise<void> => {
  const queries = kind === 'boardroom' ? boardRoomEventQueries : eventQueries
  const listKey: QueryKey = queries.getEventsPerSociety(societyId).queryKey
  const detailKey: QueryKey = queries.getEvent(eventId).queryKey
  const refreshRows = (): Promise<void[]> =>
    Promise.all(
      postIds
        .filter((postId) => isPostCached(client, postId, { inPinned: true }))
        .map((postId) => refreshPostRow(client, postId))
    )

  if (action === 'created') {
    await client.invalidateQueries({ queryKey: listKey })
  } else if (action === 'attendance') {
    await Promise.all([
      client.invalidateQueries({ queryKey: listKey }),
      client.invalidateQueries({ queryKey: detailKey }),
    ])
    const event =
      refetchedData<AnsweredEvent>(client, detailKey) ??
      refetchedData<(AnsweredEvent & { _id: string })[]>(client, listKey)?.find(
        ({ _id }) => _id === eventId
      )
    if (event && viewerId) {
      updateEventAttendance(client, eventId, viewerAttendance(event, viewerId))
    } else {
      await refreshRows()
    }
  } else {
    await Promise.all([
      client.invalidateQueries({ queryKey: listKey }),
      action === 'cancelled'
        ? client.removeQueries({ queryKey: detailKey })
        : client.invalidateQueries({ queryKey: detailKey }),
      refreshRows(),
    ])
  }
}
